/**
 * Русские сообщения об ошибках для zod-перечислений.
 *
 * ЗАЧЕМ. По умолчанию zod формирует для `z.enum` / `z.nativeEnum` текст вида
 *
 *   Invalid enum value. Expected 'EMPLOYEE' | … | 'CEO' | 'SYSADMIN', received ''
 *
 * Этот текст доходит до пользователя двумя путями:
 *   1) на клиенте — через `zodResolver` в `<FormMessage>` формы;
 *   2) с сервера — `ZodValidationPipe` кладёт `zodError.flatten()` в
 *      `details.fieldErrors`, откуда форма разворачивает его в инлайн-ошибку.
 *
 * Перечень допустимых значений в таком сообщении — это раскрытие устройства
 * системы: руководитель отдела, у которого в выпадающем списке ролей нет и не
 * может быть `CEO` / `SYSADMIN` / `TECH_DIRECTOR`, узнаёт об их существовании
 * из текста ошибки пустой формы. То же касается статусов, видов и поставщиков.
 *
 * КАК ПОЛЬЗОВАТЬСЯ. Оборачиваем объявление перечисления:
 *
 *   roleSlug: z.nativeEnum(RoleSlug, ruEnum('Выберите роль'))
 *
 * `errorMap` перекрывает ВСЕ issue данного узла (`invalid_enum_value`,
 * `invalid_type` при `undefined`, `invalid_literal`), поэтому и «не выбрано»,
 * и «прислали мусор» дают один и тот же нейтральный русский текст без
 * перечисления значений.
 */
import { z } from 'zod';

/**
 * Опции для `z.enum` / `z.nativeEnum`: единое русское сообщение вместо
 * дефолтного английского, печатающего список допустимых значений.
 */
export function ruEnum(message: string): { errorMap: z.ZodErrorMap } {
  return { errorMap: () => ({ message }) };
}

/**
 * ─── Общая карта сообщений zod ────────────────────────────────────────────
 *
 * ЗАЧЕМ. `ruEnum` закрывает только перечисления, и только там, где его не
 * забыли поставить. Всё остальное zod по умолчанию описывает по-английски и
 * языком схемы, а не языком пользователя:
 *
 *   Required
 *   Invalid uuid
 *   String must contain at least 2 character(s)
 *   Expected number, received string
 *   Unrecognized key(s) in object: 'projectId', 'catalogItemId'
 *
 * Все пять доходят до экрана: на клиенте — через `zodResolver` в
 * `<FormMessage>`, с сервера — через `ZodValidationPipe`, который кладёт
 * `zodError.flatten()` в `details.fieldErrors`, откуда форма разворачивает
 * их в инлайн-ошибку. Последняя строка вдобавок печатает пользователю
 * внутренние имена полей — ровно то раскрытие устройства системы, ради
 * которого заводился `ruEnum`.
 *
 * КАК РАБОТАЕТ. `z.setErrorMap` задаёт запасной текст для issue, у которых
 * НЕТ собственного сообщения. Явное сообщение в схеме (`.min(2, '…')`,
 * `ruEnum('…')`) всегда сильнее и остаётся как есть — карта не переписывает
 * уже написанные тексты, а лишь закрывает дыры.
 *
 * Карта ставится побочным эффектом импорта `@aris/shared` (см. `index.ts`) —
 * схемы живут здесь же. Оговорка: zod 3.25 — двойной пакет, а этот пакет
 * собран только под CommonJS, поэтому установка ложится на CJS-экземпляр;
 * потребитель в ESM, импортирующий zod напрямую, может получить второй
 * экземпляр. Для таких точек входа есть `installRuZodErrorMap()`, а сама
 * `ruZodErrorMap` экспортируется отдельно — её можно передать в
 * `z.setErrorMap` любого экземпляра (так делает `apps/web/src/main.tsx`).
 *
 * ПРАВИЛО ТЕКСТА. Сообщение говорит, ЧТО СДЕЛАТЬ, и никогда не называет
 * ни имя поля, ни имя типа, ни список допустимых значений.
 */

/** Склонение существительного после числа: 1 символ / 2 символа / 5 символов. */
function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(n) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

const SYMBOL = (n: number): string => pluralRu(n, 'символа', 'символов', 'символов');
const ELEMENT = (n: number): string => pluralRu(n, 'элемент', 'элемента', 'элементов');

/**
 * Запасные русские тексты для всех issue zod, у которых нет своего сообщения.
 *
 * Экспортируется отдельно от установки, чтобы карту можно было проверить
 * тестом напрямую, не трогая глобальное состояние zod.
 */
export const ruZodErrorMap: z.ZodErrorMap = (issue) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type: {
      // «Не прислали ничего» — это незаполненное поле, а не неверный тип.
      if (issue.received === 'undefined' || issue.received === 'null') {
        return { message: 'Заполните поле' };
      }
      // Имя ожидаемого типа («number», «string») пользователю ни о чём не
      // говорит и раскрывает устройство схемы.
      return { message: 'Некорректное значение' };
    }

    // Перечисления: НИКОГДА не печатаем список допустимых значений — он и
    // есть раскрытие устройства системы (см. шапку файла и `ruEnum`).
    case z.ZodIssueCode.invalid_enum_value:
    case z.ZodIssueCode.invalid_literal:
    case z.ZodIssueCode.invalid_union_discriminator:
      return { message: 'Выберите значение из списка' };

    // Дефолт zod печатает сами имена лишних ключей.
    case z.ZodIssueCode.unrecognized_keys:
      return { message: 'В запросе есть лишние данные' };

    case z.ZodIssueCode.invalid_string: {
      const v = issue.validation;
      if (v === 'email') return { message: 'Введите корректный адрес электронной почты' };
      if (v === 'url') return { message: 'Введите корректную ссылку' };
      if (v === 'uuid') return { message: 'Некорректный идентификатор записи' };
      if (v === 'datetime' || v === 'date' || v === 'time') {
        return { message: 'Введите корректную дату' };
      }
      if (v === 'regex') return { message: 'Значение не соответствует нужному формату' };
      if (typeof v === 'object' && v !== null) {
        if ('startsWith' in v) return { message: `Значение должно начинаться с «${v.startsWith}»` };
        if ('endsWith' in v) return { message: `Значение должно заканчиваться на «${v.endsWith}»` };
        if ('includes' in v) return { message: `Значение должно содержать «${v.includes}»` };
      }
      return { message: 'Значение не соответствует нужному формату' };
    }

    case z.ZodIssueCode.too_small: {
      const min = issue.minimum;
      const n = typeof min === 'bigint' ? Number(min) : min;
      if (issue.type === 'string') {
        if (n === 1 && issue.inclusive) return { message: 'Заполните поле' };
        return {
          message: issue.inclusive ? `Не короче ${n} ${SYMBOL(n)}` : `Длиннее ${n} ${SYMBOL(n)}`,
        };
      }
      if (issue.type === 'array') {
        if (n === 1 && issue.inclusive) return { message: 'Выберите хотя бы один вариант' };
        return {
          message: issue.inclusive
            ? `Выберите не меньше ${n} ${ELEMENT(n)}`
            : `Выберите больше ${n} ${ELEMENT(n)}`,
        };
      }
      if (issue.type === 'date') return { message: 'Выберите дату не раньше допустимой' };
      return { message: issue.inclusive ? `Не меньше ${n}` : `Больше ${n}` };
    }

    case z.ZodIssueCode.too_big: {
      const max = issue.maximum;
      const n = typeof max === 'bigint' ? Number(max) : max;
      if (issue.type === 'string') {
        return {
          message: issue.inclusive ? `Не длиннее ${n} ${SYMBOL(n)}` : `Короче ${n} ${SYMBOL(n)}`,
        };
      }
      if (issue.type === 'array') {
        return {
          message: issue.inclusive
            ? `Выберите не больше ${n} ${ELEMENT(n)}`
            : `Выберите меньше ${n} ${ELEMENT(n)}`,
        };
      }
      if (issue.type === 'date') return { message: 'Выберите дату не позже допустимой' };
      return { message: issue.inclusive ? `Не больше ${n}` : `Меньше ${n}` };
    }

    case z.ZodIssueCode.invalid_date:
      return { message: 'Введите корректную дату' };

    case z.ZodIssueCode.not_multiple_of:
      return { message: `Значение должно быть кратно ${issue.multipleOf}` };

    case z.ZodIssueCode.not_finite:
      return { message: 'Введите конечное число' };

    // `invalid_union` / `invalid_arguments` / `invalid_return_type` /
    // `invalid_intersection_types` / `custom` без своего текста дают
    // «Invalid input» — нейтрализуем одинаково.
    default:
      // Сюда попадать не должно, но если zod заведёт новый код issue,
      // лучше нейтральная русская фраза, чем внезапная английская.
      return { message: 'Некорректное значение' };
  }
};

/**
 * Ставит `ruZodErrorMap` глобально. Идемпотентна.
 *
 * Вызывается побочным эффектом импорта `@aris/shared`; отдельный экспорт
 * нужен точкам входа и тестовым setup-файлам, где важно, чтобы установка
 * была видна глазом, а не подразумевалась.
 */
export function installRuZodErrorMap(): void {
  z.setErrorMap(ruZodErrorMap);
}
